import React, { ReactNode } from 'react';
import { graphql, useStaticQuery } from 'gatsby';

// Utils
import { useReferralParams } from './referral.helper';

// Types
export type ProductTypes = 'di' | 'ltc' | 'annuity';


type PartnerDataModel = {
    source: string;
    name: string;
    phone: string | null;
    logo: string;
    tune_id: number;
    min_investment_amount?: number;
};

type CarrierDataModel = {
    // Base co-branding data
    id: number;
    logo?: string;
    name: string;
    htmlName?: string;
    products: Array<ProductTypes>;

    // Branding flag
    fullBrandning?: boolean;

    // Full co-branding data
    h1?: string;
    text?: string;
    sidebar?: {
        title: string;
        text: string;
    };
    palette?: {
        primary: string;
        accent: string;
        grey: string;
        border: string;
        sidebarBg: string;
        sidebarText: string;
    };
    fonts?: {
        heading: string;
        body: string;
    };
    hide?: {
        image: boolean;
        logos: boolean;
        quote: boolean;
    };
};

type PartnerQueryModel = {
    json: {
        carriers: Array<CarrierDataModel>;
        partners: Array<PartnerDataModel>;
    }
};

type PartnerContextModel = {
    carrier: CarrierDataModel | null;
    partner: PartnerDataModel | null;
    // setPartner: React.Dispatch<React.SetStateAction<PartnerDataModel | null>>;
    // updatePartnerData: (prop: keyof PartnerDataModel, value: any) => void;
};

const CoBrandingContext = React.createContext({} as PartnerContextModel)


export const CoBrandingContextWrapper = ({ children }: { children: ReactNode | ReactNode[] }) => {
    const { json } = useStaticQuery<PartnerQueryModel>(CarriersAndPartnersQuery);
    // const [product, setProduct] = React.useState<ProductTypes | null>(null);

    const [cbid, setCbid] = React.useState<string | null>(null);
    const [source, setSource] = React.useState<string | null>(null);
    const [tune_id, setTuneId] = React.useState<number | null>(null);


    // Cookies
    const referral = useReferralParams();


    const carrier = React.useMemo<CarrierDataModel | null>(() => {        
        if ( cbid ) {
            const foundCarrier = json?.carriers?.find(c => c.id === parseInt(cbid));
            if (foundCarrier) {
                // If fullBranding is true, return the full carrier object
                if (foundCarrier.fullBrandning) {
                    return foundCarrier;
                }
                // Otherwise, return only the base co-branding data
                return {
                    id: foundCarrier.id,
                    logo: foundCarrier.logo,
                    name: foundCarrier.name,
                    htmlName: foundCarrier.htmlName,
                    products: foundCarrier.products,
                    fullBrandning: foundCarrier.fullBrandning
                };
            }
            return null;
        }
        return null;
    }, [cbid]);


    const partner = React.useMemo<PartnerDataModel | null>(() => {
        let result: PartnerDataModel | null = null;

        if ( tune_id ) {
            result = json?.partners?.find(p => p.tune_id === tune_id) || null;
        }

        if ( !result && source ) {
            result = json?.partners?.find(p => p.source === source) || null;
        }


        return result;
    }, [source, tune_id]);

    


    // On marketing "cbid" change
    React.useEffect(() => {
        const coBrandingId = referral?.marketingCoBrandingId;

        if ( typeof window !== 'undefined' && coBrandingId && coBrandingId !== cbid ) {
            setCbid(coBrandingId);
        }
    }, [referral?.marketingCoBrandingId]);



    // On marketing "source" change
    React.useEffect(() => {
        const tuneSource = referral?.tuneSource;

        if ( typeof window !== 'undefined' && tuneSource && tuneSource !== source ) {
            setSource(tuneSource);
        }
    }, [referral?.tuneSource]);

    // On marketing "tune_id" change
    React.useEffect(() => {
        const tuneId = referral?.tunePartnerId;

        if ( typeof window !== 'undefined' && tuneId ) {
            const id = parseInt(tuneId);

            if (id !== tune_id)  {
                setTuneId(id);
            }
        }
    }, [referral?.tunePartnerId]);

    return (
        <CoBrandingContext.Provider value={{
            carrier,
            partner
        }}>
            { children }
        </CoBrandingContext.Provider>
    )
};

export const useCoBranding = () => React.useContext(CoBrandingContext);

export const useCarrierByProduct = (product: ProductTypes | null) => {
    const context = React.useContext(CoBrandingContext);

    return {
        ...(product ? (() => {            
            if ( context.carrier?.products.includes(product) ) {
                return { carrier: context.carrier }
            }
            return null;
        })() : {})
    }
};



const CarriersAndPartnersQuery = graphql`
    query PartnersQuery {
        json: partnersJson {
            carriers {
                id
                name
                logo
                htmlName
                fullBrandning
                h1
                text
                sidebar {
                    title
                    text
                }
                palette {
                    primary
                    accent
                    grey
                    border
                    sidebarBg
                    sidebarText
                }
                fonts {
                    heading
                    body
                }
                hide {
                    image
                    logos
                    quote
                }
                products
            }
            partners {
                source
                name
                phone
                logo
                tune_id
                min_investment_amount
            }
        }
    }
`;